import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createClient, updateClient, fetchClientById } from '../redux/slices/clientsSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import { Client } from '../types';
import AnefImportModal from '../components/AnefImportModal';

const ClientForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentClient, loading } = useSelector((state: RootState) => state.clients);

  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    type_client: 'particulier' as 'particulier' | 'entreprise',
    nom: '',
    prenom: '',
    raison_sociale: '',
    numero_cni: '',
    date_expiration_cni: '',
    numero_passeport: '',
    date_expiration_passeport: '',
    numero_carte_consulaire: '',
    date_expiration_carte_consulaire: '',
    telephone_principal: '',
    est_numero_etranger: false,
    telephone_secondaire: '',
    email: '',
    adresse: '',
    ville: '',
    pays: '',
    notes: '',
  });

  const [autresIdentifiants, setAutresIdentifiants] = useState<Array<{
    type: string;
    numero: string;
    date_expiration?: string;
  }>>([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [anefModalOpen, setAnefModalOpen] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchClientById(parseInt(id)));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && currentClient) {
      setFormData({
        type_client: currentClient.type_client,
        nom: currentClient.nom || '',
        prenom: currentClient.prenom || '',
        raison_sociale: currentClient.raison_sociale || '',
        numero_cni: currentClient.numero_cni || '',
        date_expiration_cni: currentClient.date_expiration_cni || '',
        numero_passeport: currentClient.numero_passeport || '',
        date_expiration_passeport: currentClient.date_expiration_passeport || '',
        numero_carte_consulaire: currentClient.numero_carte_consulaire || '',
        date_expiration_carte_consulaire: currentClient.date_expiration_carte_consulaire || '',
        telephone_principal: currentClient.telephone_principal || '',
        est_numero_etranger: currentClient.est_numero_etranger || false,
        telephone_secondaire: currentClient.telephone_secondaire || '',
        email: currentClient.email || '',
        adresse: currentClient.adresse || '',
        ville: currentClient.ville || '',
        pays: currentClient.pays || '',
        notes: currentClient.notes || '',
      });

      if (currentClient.autres_identifiants) {
        setAutresIdentifiants(currentClient.autres_identifiants);
      }
    }
  }, [currentClient, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddIdentifiant = () => {
    setAutresIdentifiants([...autresIdentifiants, { type: '', numero: '', date_expiration: '' }]);
  };

  const handleRemoveIdentifiant = (index: number) => {
    setAutresIdentifiants(autresIdentifiants.filter((_, i) => i !== index));
  };

  const handleIdentifiantChange = (index: number, field: string, value: string) => {
    const updated = [...autresIdentifiants];
    updated[index] = { ...updated[index], [field]: value };
    setAutresIdentifiants(updated);
  };

  const handleAnefImport = (clientData: any) => {
    // Pré-remplir le formulaire avec les données ANEF
    setFormData({
      type_client: clientData.type_client || 'particulier',
      nom: clientData.nom || '',
      prenom: clientData.prenom || '',
      raison_sociale: clientData.raison_sociale || '',
      numero_cni: clientData.numero_cni || '',
      date_expiration_cni: clientData.date_expiration_cni || '',
      numero_passeport: clientData.numero_passeport || '',
      date_expiration_passeport: clientData.date_expiration_passeport || '',
      numero_carte_consulaire: clientData.numero_carte_consulaire || '',
      date_expiration_carte_consulaire: clientData.date_expiration_carte_consulaire || '',
      telephone_principal: clientData.telephone_principal || '',
      est_numero_etranger: clientData.est_numero_etranger || false,
      telephone_secondaire: clientData.telephone_secondaire || '',
      email: clientData.email || '',
      adresse: clientData.adresse || '',
      ville: clientData.ville || '',
      pays: clientData.pays || '',
      notes: clientData.notes || '',
    });

    setSuccess(false);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (formData.type_client === 'particulier' && !formData.nom) {
      setError('Le nom est requis pour un particulier');
      return;
    }

    if (formData.type_client === 'entreprise' && !formData.raison_sociale) {
      setError('La raison sociale est requise pour une entreprise');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        autres_identifiants: autresIdentifiants.filter(id => id.type && id.numero),
      };

      if (isEditMode && id) {
        await dispatch(updateClient({ id: parseInt(id), data: dataToSend })).unwrap();
        setSuccess(true);
        setTimeout(() => {
          navigate(`/clients/${id}`);
        }, 1500);
      } else {
        const result = await dispatch(createClient(dataToSend)).unwrap();
        setSuccess(true);
        setTimeout(() => {
          navigate('/clients');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  if (isEditMode && loading && !currentClient) {
    return (
      <Layout>
        <div className="loading">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ marginBottom: '20px' }}>
        <Link to="/clients" style={{ textDecoration: 'none', color: '#667eea' }}>
          ← Retour à la liste
        </Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ margin: 0 }}>
          {isEditMode ? 'Modifier le client' : 'Nouveau client'}
        </h1>

        {!isEditMode && (
          <button
            type="button"
            onClick={() => setAnefModalOpen(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>☁️</span>
            Importer depuis ANEF
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">
        {isEditMode ? 'Client modifié avec succès !' : 'Client créé avec succès !'}
      </div>}

      <form onSubmit={handleSubmit}>
        {/* Type de client */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Type de client</h3>
          <div className="form-group">
            <label>Type *</label>
            <select name="type_client" value={formData.type_client} onChange={handleChange} required>
              <option value="particulier">Particulier</option>
              <option value="entreprise">Entreprise</option>
            </select>
          </div>
        </div>

        {/* Informations générales */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Informations générales</h3>

          {formData.type_client === 'particulier' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Nom *</label>
                <input type="text" name="nom" value={formData.nom} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Prénom</label>
                <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>Raison sociale *</label>
              <input type="text" name="raison_sociale" value={formData.raison_sociale} onChange={handleChange} required />
            </div>
          )}
        </div>

        {/* Contact */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Contact</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Téléphone principal</label>
              <input type="tel" name="telephone_principal" value={formData.telephone_principal} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Téléphone secondaire</label>
              <input type="tel" name="telephone_secondaire" value={formData.telephone_secondaire} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input type="checkbox" name="est_numero_etranger" checked={formData.est_numero_etranger} onChange={handleChange} style={{ width: 'auto' }} />
              Numéro étranger
            </label>
          </div>

          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Adresse</label>
            <textarea name="adresse" value={formData.adresse} onChange={handleChange} rows={3} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Ville</label>
              <input type="text" name="ville" value={formData.ville} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Pays</label>
              <input type="text" name="pays" value={formData.pays} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Documents d'identité */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Documents d'identité</h3>

          {/* CNI */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div className="form-group">
              <label>Numéro CNI</label>
              <input type="text" name="numero_cni" value={formData.numero_cni} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Date d'expiration</label>
              <input type="date" name="date_expiration_cni" value={formData.date_expiration_cni} onChange={handleChange} />
            </div>
          </div>

          {/* Passeport */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div className="form-group">
              <label>Numéro Passeport</label>
              <input type="text" name="numero_passeport" value={formData.numero_passeport} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Date d'expiration</label>
              <input type="date" name="date_expiration_passeport" value={formData.date_expiration_passeport} onChange={handleChange} />
            </div>
          </div>

          {/* Carte consulaire */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label>Numéro Carte consulaire</label>
              <input type="text" name="numero_carte_consulaire" value={formData.numero_carte_consulaire} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Date d'expiration</label>
              <input type="date" name="date_expiration_carte_consulaire" value={formData.date_expiration_carte_consulaire} onChange={handleChange} />
            </div>
          </div>

          {/* Autres identifiants */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4>Autres identifiants</h4>
              <button type="button" onClick={handleAddIdentifiant} className="btn btn-secondary" style={{ padding: '6px 12px' }}>
                + Ajouter
              </button>
            </div>

            {autresIdentifiants.map((identifiant, index) => (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  placeholder="Type (ex: Permis)"
                  value={identifiant.type}
                  onChange={(e) => handleIdentifiantChange(index, 'type', e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <input
                  type="text"
                  placeholder="Numéro"
                  value={identifiant.numero}
                  onChange={(e) => handleIdentifiantChange(index, 'numero', e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <input
                  type="date"
                  value={identifiant.date_expiration}
                  onChange={(e) => handleIdentifiantChange(index, 'date_expiration', e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <button type="button" onClick={() => handleRemoveIdentifiant(index)} className="btn btn-danger" style={{ padding: '8px 12px' }}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>Notes</h3>
          <div className="form-group">
            <textarea name="notes" value={formData.notes} onChange={handleChange} rows={5} placeholder="Notes additionnelles..." />
          </div>
        </div>

        {/* Boutons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Link to="/clients" className="btn btn-secondary">
            Annuler
          </Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Enregistrement...' : (isEditMode ? 'Modifier' : 'Créer')}
          </button>
        </div>
      </form>

      <AnefImportModal
        open={anefModalOpen}
        onClose={() => setAnefModalOpen(false)}
        onImportSuccess={handleAnefImport}
      />
    </Layout>
  );
};

export default ClientForm;
